import type { FC, MouseEvent } from 'react';
import React, { memo, useEffect, useState } from 'react';
import classnames from 'classnames';
import { Switch } from 'antd';
import { LanguageType } from 'common/constants';
import { AppRoutes } from 'common/types';
import { NavigationItem } from 'components/NavigationPanel/atoms/NavigationItem/NavigationItem';
import {
  carsActions,
  generalSliceActions,
  generalSliceSelectors,
  ordersActions,
  usersActions,
} from 'store/slices';
import { useAppDispatch, useAppSelector } from 'store';
import { useTranslations } from 'hooks/useTranslations';
import { useCheckMobile } from 'common/helpers/checkIsMobile';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useIsScrolled } from 'hooks/useIsScrolled';
import { ArrowLeftOnRectangleIcon } from '@heroicons/react/20/solid';
import { CarRentalIcon } from 'icons/clientDashboard/Aside/links/CarRental';
import { BurgerButton } from './atoms/BurgerButton';
import type { NavItems } from './types';
import styles from './MenuWithBurger.module.scss';
type MenuWithBurgerProps = {
  isHovered?: boolean;
  label?: string;
  hideTranslations?: boolean;
  menuItems: NavItems;
  isAdminPage?: boolean;
};
export const MenuWithBurger: FC<MenuWithBurgerProps> = memo(
  ({ isAdminPage, label, isHovered, menuItems, hideTranslations }) => {
    const isScrolled = useIsScrolled();
    const navigate = useNavigate();
    const isMobile = useCheckMobile();
    const dispatch = useAppDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();
    const temporaryLanguage = useAppSelector(
      generalSliceSelectors.selectTemporaryLanguage,
    );
    const { t } = useTranslations();
    const toggleMenu = () => {
      document.body.style.overflow = isOpen ? '' : 'hidden';
      setIsOpen(!isOpen);
    };

    const handleSwitchLanguages = () => {
      dispatch(
        generalSliceActions.setTemporaryLanguage(
          temporaryLanguage === LanguageType.EN
            ? LanguageType.FR
            : LanguageType.EN,
        ),
      );
    };
    const handleOutsideClick = () => {
      document.body.style.overflow = isOpen ? '' : 'hidden';
      setIsOpen(!isOpen);
    };
    const handleLogout = () => {
      dispatch(usersActions.logout());
      navigate(AppRoutes.HOME);
    };

    const handleClearOnNavigate = () => {
      dispatch(carsActions.setSelectedCar(null));
      dispatch(ordersActions.setSelectedOrder(null));
    };
    const handleNavigate = (ev: MouseEvent<HTMLAnchorElement>) => {
      ev.preventDefault();
      handleClearOnNavigate();
      navigate(AppRoutes.RESERVATION);
      window.scrollTo({
        top: 0,
      });
    };
    useEffect(() => {
      setIsOpen(false);
      document.body.style.overflow = '';
      //eslint-disable-next-line
    }, [location.pathname]);

    return (
      <div
        className={classnames({
          'px-4 py-2.5 fixed w-full top-0 z-[99]': isAdminPage,
          'bg-white': isScrolled && isAdminPage,
        })}
      >
        <div className={styles.menuWithBurger}>
          <BurgerButton
            isOpen={isOpen}
            label={label}
            toggleMenu={toggleMenu}
            bgBlack={
              isScrolled ||
              isOpen ||
              location.pathname === AppRoutes.RESERVATION ||
              isHovered
            }
          />
          <div
            className={classnames(styles.menuSideWrapper, {
              [styles.openedMenuSide]: isOpen,
              '!bg-[#f1f1f1]': isAdminPage,
            })}
          >
            {menuItems.map((item) => (
              <NavigationItem
                key={item.link}
                translate={t}
                Icon={item.icon}
                translationKey={item.translationKey}
                link={item.link}
                isAdminPage={isAdminPage}
              />
            ))}

            {isAdminPage && (
              <>
                <div className="flex items-center py-[6px] px-[10px]">
                  <Link
                    to={AppRoutes.RESERVATION}
                    className="flex items-center w-full cursor-pointer text-primaryBlack py-[8px] px-[15px] rounded-md"
                    onClick={handleNavigate}
                  >
                    <CarRentalIcon width={20} height={20} />
                    <span className="text-md ml-[10px]">
                      {t('ADMIN_NAVIGATION_RESERVATION')}
                    </span>
                  </Link>
                </div>
                <div className="flex items-center py-[6px] px-[10px]">
                  <div
                    className="flex items-center w-full cursor-pointer text-primaryBlack py-[8px] px-[15px] rounded-md"
                    onClick={handleLogout}
                  >
                    <ArrowLeftOnRectangleIcon width={20} height={20} />
                    <span className="text-md ml-[10px]">
                      {t('LOGOUT_CONTENT')}
                    </span>
                  </div>
                </div>
              </>
            )}

            {isMobile && !hideTranslations && (
              <div className="py-4 px-5 flex gap-3 items-center">
                <span>{t('LANGUAGE')}</span>
                <Switch
                  checkedChildren="France"
                  unCheckedChildren="English"
                  className="bg-[#E2E2E2]"
                  checked={temporaryLanguage === LanguageType.FR}
                  onChange={handleSwitchLanguages}
                />
              </div>
            )}
          </div>
          {isOpen && (
            <span onClick={handleOutsideClick} className={styles.backdrop} />
          )}
        </div>
      </div>
    );
  },
);
